/**
 * Vincent B905 (Eric 2026-02-14 SPEC STRICK PROD) — Page admin
 * de fusion des tags quasi-doublons.
 *
 * Flow :
 *   1. Charge les paires détectées via `GET /api/tags/merge-suggestions`.
 *   2. Chaque paire propose : garder le plus fréquent, retirer l'autre.
 *      Vincent peut inverser d'un clic (bouton ↔) OU fusionner directement.
 *   3. `POST /api/tags/merge` remplace `drop` → `keep` sur toutes les
 *      vidéos de l'org active. Idempotent.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TagMergesAdminPage() {
  // Tâche 1437 (Eric 2026-10-02) — La page entière était en français : elle
  // n'appelait `useLang` nulle part.
  const { tr } = useLang();
  const [pairs, setPairs] = useState([]);
  const [totalTags, setTotalTags] = useState(0);
  const [threshold, setThreshold] = useState(0.85);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // key = drop:keep en cours
  const [dismissed, setDismissed] = useState(new Set());
  // Vincent B908 (Eric 2026-02-14 SPEC STRICK PROD) — Règles d'auto-merge.
  const [rules, setRules] = useState({
    plural_singular: false,
    accent_variants: false,
    case_only: false,
  });
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastApplyReport, setLastApplyReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/tags/merge-suggestions`,
        { params: { threshold }, withCredentials: true },
      );
      setPairs(data?.pairs || []);
      setTotalTags(data?.total_tags || 0);
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  // Vincent B908 — Charge les règles d'auto-merge au montage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRulesLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/tags/auto-merge/rules`, { withCredentials: true });
        if (!cancelled) {
          setRules({
            plural_singular: !!data?.plural_singular,
            accent_variants: !!data?.accent_variants,
            case_only: !!data?.case_only,
          });
        }
      } catch { /* silencieux */ }
      finally { if (!cancelled) setRulesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleRule = async (key) => {
    if (rulesSaving) return;
    const next = { ...rules, [key]: !rules[key] };
    setRules(next);
    setRulesSaving(true);
    try {
      await axios.put(`${API}/api/tags/auto-merge/rules`, next, { withCredentials: true });
    } catch (err) {
      setRules(rules);
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setRulesSaving(false);
    }
  };

  const applyRulesNow = async () => {
    if (applying) return;
    const activeCount = Object.values(rules).filter(Boolean).length;
    if (activeCount === 0) {
      toast.info(tr("Activez au moins une règle avant d'appliquer.",
                    "Enable at least one rule before applying."));
      return;
    }
    setApplying(true);
    setLastApplyReport(null);
    try {
      const { data } = await axios.post(`${API}/api/tags/auto-merge/apply`, {}, { withCredentials: true });
      setLastApplyReport(data);
      const n = data?.total_pairs || 0;
      toast.success(n === 0
        ? tr("Aucune paire à fusionner selon les règles activées.",
             "No pair to merge under the enabled rules.")
        : tr(`${n} paire(s) fusionnée(s).`, `${n} pair(s) merged.`));
      await load(); // Reload suggestions manuelles (elles diminueront).
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setApplying(false);
    }
  };

  const doMerge = async (keep, drop) => {
    const key = `${drop}::${keep}`;
    if (merging) return;
    setMerging(key);
    try {
      const { data } = await axios.post(
        `${API}/api/tags/merge`,
        { keep, drop },
        { withCredentials: true },
      );
      toast.success(tr(
        `« ${drop} » → « ${keep} » — ${data?.videos_updated || 0} vidéo(s) mise(s) à jour`,
        `“${drop}” → “${keep}” — ${data?.videos_updated || 0} video(s) updated`,
      ));
      setPairs((prev) => prev.filter((p) => !(p.keep === keep && p.drop === drop)));
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setMerging(null);
    }
  };

  const dismissPair = (keep, drop) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(`${drop}::${keep}`);
      return next;
    });
  };

  const visiblePairs = pairs.filter(
    (p) => !dismissed.has(`${p.drop}::${p.keep}`),
  );

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="tag-merges-admin-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">
            {tr("🔗 Fusion de tags quasi-doublons", "🔗 Near-duplicate tag merge")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            {tr("Détecte les tags très proches (pluriel, accents, coquilles) dans votre organisation. Fusionne d'un clic pour garder un vocabulaire compact.",
                "Finds very close tags (plural, accents, typos) in your organisation. Merge in one click to keep a compact vocabulary.")}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="tag-merges-refresh-btn"
        >
          {loading ? tr("⏳ Chargement…", "⏳ Loading…") : tr("↻ Recharger", "↻ Reload")}
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded p-3">
        <label className="text-xs font-semibold text-zinc-700" htmlFor="tag-merge-threshold-input">
          {tr("Seuil de similarité", "Similarity threshold")}
        </label>
        <input
          id="tag-merge-threshold-input"
          type="range"
          min="0.70"
          max="1.00"
          step="0.05"
          value={threshold}
          onChange={(e) => setThreshold(parseFloat(e.target.value))}
          className="flex-1 max-w-[260px]"
          data-testid="tag-merges-threshold-slider"
        />
        <span className="text-xs font-mono text-zinc-800 tabular-nums">
          {Math.round(threshold * 100)}%
        </span>
        <span className="text-[10px] text-zinc-500 ml-auto">
          {tr(`${totalTags} tag(s) dans l'org · ${visiblePairs.length} paire(s) détectée(s)`,
              `${totalTags} tag${totalTags === 1 ? "" : "s"} in the org · ${visiblePairs.length} pair${visiblePairs.length === 1 ? "" : "s"} found`)}
        </span>
      </div>

      {/* Vincent B908 (Eric 2026-02-14 SPEC STRICK PROD) — Règles
          d'auto-merge one-shot. Vincent active les règles souhaitées,
          puis clique « Appliquer maintenant » : les paires détectées
          par ces règles sont fusionnées d'un coup, sans clic par paire. */}
      <div
        className="mb-4 bg-violet-50 border border-violet-200 rounded p-3"
        data-testid="auto-merge-rules-section"
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <h2 className="text-sm font-semibold text-violet-900">
            {tr("⚙️ Règles d'auto-merge", "⚙️ Auto-merge rules")}
          </h2>
          <button
            type="button"
            onClick={applyRulesNow}
            disabled={applying || rulesLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50"
            data-testid="auto-merge-apply-btn"
          >
            {applying ? tr("⏳ Application…", "⏳ Applying…") : tr("▶ Appliquer maintenant", "▶ Apply now")}
          </button>
        </div>
        {rulesLoading ? (
          <p className="text-xs text-violet-700 italic">{tr("Chargement…", "Loading…")}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[
              // Tâche 1437 — Les EXEMPLES sont des données d'illustration :
              // on en donne l'équivalent anglais plutôt que de traduire mot à
              // mot « retraité », qui ne montrerait plus rien d'un accent.
              { key: "case_only",
                label: tr("Casse seule", "Case only"),
                desc: tr("« REER » et « reer » → même tag", "“RRSP” and “rrsp” → same tag") },
              { key: "accent_variants",
                label: tr("Variantes d'accent", "Accent variants"),
                desc: tr("« retraité » et « retraite » → même tag", "“résumé” and “resume” → same tag") },
              { key: "plural_singular",
                label: tr("Pluriels/Singuliers", "Plurals/Singulars"),
                desc: tr("« recrutement » et « recrutements » → même tag", "“investment” and “investments” → same tag") },
            ].map((r) => (
              <label
                key={r.key}
                className={`inline-flex items-center gap-2 px-2 py-1 rounded border text-xs cursor-pointer transition-colors ${
                  rules[r.key]
                    ? "bg-violet-100 border-violet-400 text-violet-900"
                    : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}
                title={r.desc}
                data-testid={`auto-merge-rule-${r.key}-label`}
              >
                <input
                  type="checkbox"
                  checked={rules[r.key]}
                  onChange={() => toggleRule(r.key)}
                  disabled={rulesSaving}
                  data-testid={`auto-merge-rule-${r.key}-checkbox`}
                  className="accent-violet-700"
                />
                <span className="font-semibold">{r.label}</span>
                <span className="text-[10px] text-zinc-500">— {r.desc}</span>
              </label>
            ))}
          </div>
        )}
        {lastApplyReport && (lastApplyReport.applied || []).length > 0 && (
          <div className="mt-2 text-[11px] text-violet-900" data-testid="auto-merge-last-report">
            <p className="font-semibold mb-1">
              {tr(`Dernière application : ${lastApplyReport.total_pairs} paire(s)`,
                  `Last run: ${lastApplyReport.total_pairs} pair${lastApplyReport.total_pairs === 1 ? "" : "s"}`)}
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {lastApplyReport.applied.map((p) => (
                <li key={`${p.drop}::${p.keep}`}>
                  {tr(`« ${p.drop} » → « ${p.keep} » (${p.rule} · ${p.videos_updated} vidéo(s))`,
                      `“${p.drop}” → “${p.keep}” (${p.rule} · ${p.videos_updated} video${p.videos_updated === 1 ? "" : "s"})`)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {visiblePairs.length === 0 && !loading ? (
        <p className="text-sm text-zinc-500 italic bg-white border border-zinc-200 rounded p-6 text-center" data-testid="tag-merges-empty">
          {tr("✓ Aucune paire quasi-doublons détectée avec ce seuil.",
              "✓ No near-duplicate pair found at this threshold.")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="tag-merges-list">
          {visiblePairs.map((p) => {
            const key = `${p.drop}::${p.keep}`;
            return (
              <li
                key={key}
                className="flex items-center gap-3 bg-white border border-zinc-200 rounded p-3"
                data-testid={`tag-merge-pair-${p.keep}-${p.drop}`}
              >
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="text-xs bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                    ✓ {p.keep} <span className="text-teal-500 font-mono">({p.keep_count})</span>
                  </span>
                  <span className="text-zinc-400" aria-hidden="true">←</span>
                  <span className="text-xs bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                    ✕ {p.drop} <span className="text-red-500 font-mono">({p.drop_count})</span>
                  </span>
                  <span className="text-[10px] text-zinc-500 ml-2">{tr("sim. ", "sim. ")}{Math.round(p.similarity * 100)}%</span>
                </div>
                <button
                  type="button"
                  onClick={() => doMerge(p.drop, p.keep) /* SWAP */}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                  title={tr("Inverser : garder l'autre à la place", "Swap: keep the other one instead")}
                  data-testid={`tag-merge-swap-btn-${p.keep}-${p.drop}`}
                >
                  {tr("↔ Inverser", "↔ Swap")}
                </button>
                <button
                  type="button"
                  onClick={() => dismissPair(p.keep, p.drop)}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                  title={tr("Ignorer cette paire pour cette session", "Ignore this pair for this session")}
                  data-testid={`tag-merge-dismiss-btn-${p.keep}-${p.drop}`}
                >
                  {tr("Ignorer", "Ignore")}
                </button>
                <button
                  type="button"
                  onClick={() => doMerge(p.keep, p.drop)}
                  disabled={merging === key}
                  className="text-[11px] font-semibold px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                  data-testid={`tag-merge-apply-btn-${p.keep}-${p.drop}`}
                >
                  {merging === key ? "⏳" : tr("Fusionner", "Merge")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
